import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";

import { api, refreshAccessToken } from "@/lib/api/client";
import { API_ORIGIN } from "@/lib/config";
import { getAccessToken } from "@/lib/auth/tokens";
import { getOnBehalfOf, onBehalfKey } from "@/lib/api/on-behalf";
import { actionPlanKeys } from "@/features/action-plans/api/keys";
import { unwrap } from "@/lib/api/unwrap";
import { queryKeys } from "@/lib/query/keys";
import i18n from "@/lib/i18n/i18n";
import type { HistoryResponse } from "@/components/HistoryDrawer";
import type { components } from "@/lib/api/schema.gen";

import type { DirectoryFlags } from "@/features/resources";

import type {
  Action,
  ActionAnswer,
  AnswerType,
  AvailableOptionalIssue,
  Characteristic,
  CompanySize,
  EnterpriseProfileCreate,
  EnterpriseProfileDetail,
  Issue,
  QuestionnaireProgress,
  ResultThematic,
  Sector,
  UnavoidableIssue,
} from "./types";

/**
 * Namespaced query keys for the questionnaire slice. Kept local to the feature
 * (rather than in the shared factory) so the slice owns its own invalidation.
 */
export const qKeys = {
  // Getter so keys pick up the current on-behalf member (partner mode): a
  // member's cache never collides with the partner's own. Self-mode → unchanged.
  get all() {
    return ["questionnaire", ...onBehalfKey()] as const;
  },
  sectors: () => [...qKeys.all, "sectors"] as const,
  characteristics: () => [...qKeys.all, "characteristics"] as const,
  companySizes: () => [...qKeys.all, "company-sizes"] as const,
  issues: () => [...qKeys.all, "issues"] as const,
  profile: (id: string | null) => [...qKeys.all, "profile", id] as const,
  unavoidableIssues: () => [...qKeys.all, "unavoidable-issues"] as const,
  optionalIssues: () => [...qKeys.all, "optional-issues"] as const,
  // Under optionalIssues() so adding or removing a choice refreshes it too.
  availableOptionalIssues: () =>
    [...qKeys.optionalIssues(), "available"] as const,
  progress: () => [...qKeys.all, "progress"] as const,
  actions: (goalId: string) => [...qKeys.all, "actions", goalId] as const,
  answers: (goalId: string) => [...qKeys.all, "answers", goalId] as const,
  allAnswers: () => [...qKeys.all, "answers"] as const,
  results: () => [...qKeys.all, "results"] as const,
  history: () => [...qKeys.all, "history"] as const,
  memberHistory: () => [...qKeys.all, "member-history"] as const,
} as const;

/**
 * What a saved answer makes stale: the progress and results read the answers,
 * and so does step 3 of every action plan — a course of action's 1–5
 * importance is the rating step 3 shows.
 */
export function invalidateAfterAnswersSaved(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: qKeys.progress() });
  void qc.invalidateQueries({ queryKey: qKeys.results() });
  void qc.invalidateQueries({ queryKey: actionPlanKeys.all });
}

// --- Definition (read-only reference data) -----------------------------------

export function useSectors() {
  return useQuery({
    queryKey: qKeys.sectors(),
    queryFn: () => unwrap(api.GET("/api/v1/questionnaire/sectors/")) as Promise<Sector[]>,
  });
}

export function useCharacteristics() {
  return useQuery({
    queryKey: qKeys.characteristics(),
    queryFn: () =>
      unwrap(api.GET("/api/v1/questionnaire/characteristics/")) as Promise<
        Characteristic[]
      >,
  });
}

export function useCompanySizes() {
  return useQuery({
    queryKey: qKeys.companySizes(),
    queryFn: () =>
      unwrap(api.GET("/api/v1/questionnaire/company-sizes/")) as Promise<CompanySize[]>,
  });
}

export function useIssues() {
  return useQuery({
    queryKey: qKeys.issues(),
    queryFn: () => unwrap(api.GET("/api/v1/questionnaire/issues/")) as Promise<Issue[]>,
  });
}

// --- Enterprise profile ------------------------------------------------------

export function useProfile(id: string | null) {
  return useQuery({
    queryKey: qKeys.profile(id),
    enabled: id != null,
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/enterprise-profiles/{id}/", {
          params: { path: { id: id as string } },
        }),
      ) as Promise<EnterpriseProfileDetail>,
  });
}

export function useCreateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<EnterpriseProfileCreate>) =>
      unwrap(
        api.POST("/api/v1/enterprise-profiles/", {
          body: body as EnterpriseProfileCreate,
        }),
      ) as Promise<EnterpriseProfileCreate>,
    onSuccess: async (created) => {
      void qc.invalidateQueries({ queryKey: qKeys.all });
      // Write the new id into the cached `/auth/me` straight away. Awaiting the
      // refetch below is not enough on its own: the caller navigates as soon as
      // this resolves, and `/questionnaire`'s gate seeds its profile id from a
      // `useState` initializer that runs on mount — before React has propagated
      // the refetched user through context. Patching the cache makes the value
      // right in the very first render of the next route.
      qc.setQueryData(
        queryKeys.auth.me(),
        (old: { enterprise_profile_id?: string | null } | undefined) =>
          old ? { ...old, enterprise_profile_id: created.id } : old,
      );
      // Creating the profile changes what `/auth/me` reports: until that is
      // refetched, `user.enterprise_profile_id` is still null in a cache held
      // at `staleTime: Infinity`. Any caller that navigates somewhere deriving
      // the profile id from `useAuth()` — `/questionnaire`'s own gate seeds it
      // from a `useState` initializer, read once — would land back on the
      // creation form it just came from. The original caller hid this by
      // keeping the new id in local state instead.
      //
      // **Awaited, not fired and forgotten.** TanStack waits on `onSuccess`
      // before dispatching success, so returning a promise is what holds the
      // caller's own `onSuccess` — and the navigation it triggers — until the
      // refetch has landed. `auth-context.tsx`'s `login()` awaits the same
      // invalidation for the same reason.
      await qc.invalidateQueries({ queryKey: queryKeys.auth.me() });
    },
  });
}

/** Persist a partial patch to the enterprise profile (used by autosave). */
export async function patchProfile(
  id: string,
  body: components["schemas"]["PatchedEnterpriseProfileDetail"],
  signal?: AbortSignal,
): Promise<EnterpriseProfileDetail> {
  return unwrap(
    api.PATCH("/api/v1/enterprise-profiles/{id}/", {
      params: { path: { id } },
      body,
      signal,
    }),
  ) as Promise<EnterpriseProfileDetail>;
}

// --- Issues (unavoidable + optional) -----------------------------------------

export function useUnavoidableIssues() {
  return useQuery({
    queryKey: qKeys.unavoidableIssues(),
    queryFn: () =>
      unwrap(api.GET("/api/v1/questionnaire/unavoidable-issues/")) as Promise<
        UnavoidableIssue[]
      >,
  });
}

export function useOptionalIssues() {
  return useQuery({
    queryKey: qKeys.optionalIssues(),
    queryFn: async () => {
      const page = await unwrap(
        api.GET("/api/v1/questionnaire/optional-issues/"),
      );
      return (page as components["schemas"]["PaginatedOptionalIssueChoiceList"])
        .results;
    },
  });
}

/** The issues the member can still add — the server applies the same rule as
 * the Django picker, so each one appears in the questionnaire once chosen. */
export function useAvailableOptionalIssues() {
  return useQuery({
    queryKey: qKeys.availableOptionalIssues(),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/questionnaire/optional-issues/available/"),
      ) as Promise<AvailableOptionalIssue[]>,
  });
}

export function useAddOptionalIssue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (issueId: string) =>
      unwrap(
        api.POST("/api/v1/questionnaire/optional-issues/", {
          body: { issue: issueId } as components["schemas"]["OptionalIssueChoice"],
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qKeys.optionalIssues() });
      void qc.invalidateQueries({ queryKey: qKeys.progress() });
    },
  });
}

// --- Answering ---------------------------------------------------------------

export function useProgress(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: qKeys.progress(),
    enabled: options?.enabled,
    queryFn: () =>
      unwrap(api.GET("/api/v1/questionnaire/progress/")) as Promise<
        QuestionnaireProgress
      >,
  });
}

/**
 * The enterprise's three resource-directory flags, for the Ressources entry
 * point on a piste d'action.
 *
 * Read off the already-cached progress query rather than fetched: both
 * autodiagnostic surfaces mount `useProgress` at their top level, so this costs
 * no request and saves drilling three booleans down to every action row. All
 * false until progress resolves, and false server-side when acting on behalf of
 * a member.
 */
export function useResourceDirectoryFlags(): DirectoryFlags {
  const progress = useProgress();
  return {
    expert_directory_enabled: progress.data?.expert_directory_enabled ?? false,
    tool_directory_enabled: progress.data?.tool_directory_enabled ?? false,
    financial_aid_directory_enabled:
      progress.data?.financial_aid_directory_enabled ?? false,
  };
}

/**
 * Query options (key + fetcher) for a goal's actions and answers. Shared by the
 * hooks below and by the focus-mode next-goal prefetch (see FocusMode) so the
 * prefetched cache entry lands under the exact key the hook then reads.
 */
export function goalActionsQuery(goalId: string) {
  return {
    queryKey: qKeys.actions(goalId),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/questionnaire/actions/", {
          params: { query: { goal_id: goalId } },
        }),
      ) as Promise<Action[]>,
  };
}

export function goalAnswersQuery(goalId: string) {
  return {
    queryKey: qKeys.answers(goalId),
    queryFn: async () => {
      const page = await unwrap(
        api.GET("/api/v1/questionnaire/answers/", {
          params: { query: { goal_id: goalId } },
        }),
      );
      return (page as components["schemas"]["PaginatedActionAnswerList"]).results;
    },
  };
}

export function useGoalActions(goalId: string | null) {
  return useQuery({
    ...goalActionsQuery(goalId ?? ""),
    enabled: goalId != null,
  });
}

export function useGoalAnswers(goalId: string | null) {
  return useQuery({
    ...goalAnswersQuery(goalId ?? ""),
    enabled: goalId != null,
  });
}

export interface BulkAnswerInput {
  action: string;
  answer: AnswerType;
  comment?: string;
  show_comment_publicly?: boolean;
  /** 1–5 importance rating (null = not rated). */
  priority?: number | null;
}

/** Persist all answered actions for a goal in one request (autosave sink). */
export async function bulkSubmitAnswers(
  goalId: string,
  answers: BulkAnswerInput[],
  signal?: AbortSignal,
): Promise<ActionAnswer[]> {
  return unwrap(
    api.POST("/api/v1/questionnaire/bulk-submit/", {
      body: {
        goal_id: goalId,
        answers: answers as unknown as { [key: string]: unknown }[],
      },
      signal,
    }),
  ) as Promise<ActionAnswer[]>;
}

// --- Results -----------------------------------------------------------------

export function useResults() {
  return useQuery({
    queryKey: qKeys.results(),
    queryFn: () =>
      unwrap(api.GET("/api/v1/questionnaire/results/")) as Promise<ResultThematic[]>,
  });
}

/**
 * Download the results PDF as a JWT-authenticated blob (the endpoint returns
 * application/pdf, not JSON, so it can't go through the typed client). Mirrors
 * the reports CSV download path: bearer token + refresh-on-401 retry.
 */
export async function downloadResultsPdf(filename: string): Promise<void> {
  const onBehalfOf = getOnBehalfOf();
  const url =
    `${API_ORIGIN}/api/v1/questionnaire/results/pdf/` +
    (onBehalfOf ? `?on_behalf_of=${onBehalfOf}` : "");
  const request = (token: string | null) =>
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });

  let response = await request(getAccessToken());
  if (response.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed) response = await request(refreshed);
  }
  if (!response.ok) {
    throw new Error(`PDF export failed (${response.status})`);
  }
  const blob = await response.blob();
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(href);
}

/**
 * Persist the group member's partner-consent decision (K-3 mandatory gate).
 * Mirrors the old app's member_consent step: consultation (diagnostic) + edit
 * flags — a timestamp when granted, null otherwise. The server stamps the
 * always-on progress baseline on this write, which clears `needs_partner_consent`.
 */
export function saveConsentDecision(input: {
  diagnostic: boolean;
  edit: boolean;
}): Promise<unknown> {
  const now = new Date().toISOString();
  return unwrap(
    api.PATCH("/api/v1/consents/me/", {
      body: {
        consent_diagnostic_at: input.diagnostic ? now : null,
        consent_edit_at: input.edit ? now : null,
      } as components["schemas"]["PatchedConsent"],
    }),
  );
}

// --- Change history ----------------------------------------------------------

/** One member's self-assessment history, or everything that happened to them.
 *
 * Both endpoints are untyped `APIView`s, so these go through `fetch` like
 * `useToolHistory` and `useActionPlanHistory` rather than the generated client.
 * The server renders the field labels per `Accept-Language`, so the language is
 * part of the query key: switching it must refetch, not serve a stale locale.
 * The on-behalf param is what lets a partner read a member's history.
 */
function historyQuery(path: string, key: readonly unknown[], enabled: boolean) {
  return {
    queryKey: [...key, i18n.language] as const,
    enabled,
    queryFn: async (): Promise<HistoryResponse> => {
      const onBehalfOf = getOnBehalfOf();
      const url =
        `${API_ORIGIN}${path}` + (onBehalfOf ? `?on_behalf_of=${onBehalfOf}` : "");
      const res = await fetch(url, {
        headers: {
          ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}),
          "Accept-Language": i18n.language,
        },
      });
      if (!res.ok) throw new Error("HTTP " + res.status);
      return (await res.json()) as HistoryResponse;
    },
  };
}

export function useQuestionnaireHistory(enabled: boolean) {
  return useQuery(
    historyQuery("/api/v1/questionnaire/history/", qKeys.history(), enabled),
  );
}

export function useMemberHistory(enabled: boolean) {
  return useQuery(
    historyQuery(
      "/api/v1/enterprise-profiles/history/",
      qKeys.memberHistory(),
      enabled,
    ),
  );
}
